<script lang="ts" setup>
import { faFilePdf } from '@fortawesome/pro-regular-svg-icons';
import { faFilm } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import SectionHeader from '@/components/SectionHeader.vue';
import type { GetProjectDetailsProjectResponse } from '@/api';

const { project, modelId = 0 } = defineProps<{
  project: GetProjectDetailsProjectResponse;
  modelId?: number;
}>();

const { t } = useI18n();

const data = computed(() => {
  const model = project.models.find((model) => model.id === modelId);
  const linkSource = modelId > 0 ? model : project;

  return {
    description: (model && model.description.length > 0 ? model.description : project.description).toHtmlContent(),
    document: linkSource?.document ?? null,
    multimedia: linkSource?.multimedia ?? null,
  };
});

// Le texte reste toujours dans le HTML : seul son affichage est écourté, ce qui
// préserve l'indexation tout en évitant un mur de texte.
const content = ref<HTMLElement | null>(null);
const expanded = ref(false);
const overflows = ref(false);

const measure = async () => {
  await nextTick();
  const element = content.value;
  if (!element) return;
  overflows.value = element.scrollHeight > element.clientHeight + 8;
};

onMounted(measure);

watch(
  () => data.value.description,
  () => {
    expanded.value = false;
    void measure();
  }
);
</script>

<template>
  <section>
    <SectionHeader>{{ t('views.project.description.header') }}</SectionHeader>
    <div>
      <div class="mb-6">
        <div ref="content" :class="['markdown text-base text-gray-700', expanded ? '' : 'max-h-80 overflow-hidden print:max-h-none print:overflow-visible']" v-html="data.description" />
        <button
          v-if="overflows"
          class="text-ph-green-700 hover:text-ph-green-600 focus:outline-ph-green-600 no-print mt-2 cursor-pointer rounded-md text-sm font-semibold focus:outline-2 focus:outline-offset-2"
          type="button"
          @click="expanded = !expanded"
        >
          {{ expanded ? t('views.project.description.show-less') : t('views.project.description.show-more') }}
        </button>
      </div>
      <div v-if="data.document && data.document.length > 0" class="mb-1">
        <a :href="data.document" rel="noopener noreferrer" target="_blank">
          <div class="hover:bg-ph-green-600 bg-ph-green-700 inline-block rounded-lg px-4 py-2 text-white">
            <FontAwesomeIcon :icon="faFilePdf" class="text-ph-green-200 mr-1" />
            {{ t('views.project.description.document') }}
          </div>
        </a>
      </div>
      <div v-if="data.multimedia && data.multimedia.length > 0" class="mb-1">
        <a :href="data.multimedia" rel="noopener noreferrer" target="_blank">
          <div class="hover:bg-ph-green-600 bg-ph-green-700 inline-block rounded-lg px-4 py-2 text-white">
            <FontAwesomeIcon :icon="faFilm" class="text-ph-green-200 mr-1" />
            {{ t('views.project.description.multimedia') }}
          </div>
        </a>
      </div>
    </div>
  </section>
</template>

<style lang="css" scoped></style>
